import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AbsencesService } from '@wilson/absences';
import { Absence, AbsenceStates } from '@wilson/interfaces';
import {
  SetDemarcatorRange,
  TimelineRemoveAbsence,
  TimelineUpdateAbsence,
} from '@wilson/shift-timeline/state';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from './timeline-common-operations.service';
import { AbsenceDrawerHelperService } from '@wilson/non-domain-specific/absence';

@Injectable({
  providedIn: 'root',
})
export class TimelineAbsencesService {
  private readonly absencesService = inject(AbsencesService);
  private readonly translate = inject(TranslateService);
  private readonly message = inject(NzMessageService);
  private readonly store = inject(Store);
  private readonly absenceDrawerHelperService = inject(
    AbsenceDrawerHelperService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );

  private onGoingRequest = new BehaviorSubject<boolean>(false);
  public absenceBeingUpdated$ = this.onGoingRequest.asObservable();

  async updateTimelineAbsence(
    absence: Absence,
    status: AbsenceStates,
  ): Promise<void> {
    try {
      this.onGoingRequest.next(true);
      await firstValueFrom(
        this.absencesService.updateAbsence({ status }, absence.id as string),
      );
      this.store.dispatch(
        new TimelineUpdateAbsence({ ...absence, status } as Absence & {
          id: string;
          isHighlighted: boolean;
        }),
      );
      this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
        absence.userId,
      ]);
      this.message.success(
        this.translate.instant('general.saved_successfully'),
      );
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } finally {
      this.onGoingRequest.next(false);
    }
  }

  async removeTimelineAbsence(
    absence: Absence,
    status: AbsenceStates,
  ): Promise<void> {
    try {
      this.onGoingRequest.next(true);
      if (absence.id) {
        await firstValueFrom(
          this.absencesService.updateAbsence({ status }, absence.id),
        );

        this.store.dispatch(
          new TimelineRemoveAbsence({
            absenceId: absence.id,
            userId: absence.userId,
          }),
        );
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          absence.userId,
        ]);
        this.message.success(
          this.translate.instant('general.saved_successfully'),
        );
        this.absenceDrawerHelperService.toggleAbsenceDrawer(false);
        this.store.dispatch([new SetDemarcatorRange([])]);
      } else {
        this.message.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } finally {
      this.onGoingRequest.next(false);
    }
  }

  async deleteAbsence(absence: Absence): Promise<void> {
    try {
      this.onGoingRequest.next(true);
      await firstValueFrom(
        this.absencesService.deleteAbsence(absence.id as string),
      );

      this.store.dispatch(
        new TimelineRemoveAbsence({
          absenceId: absence.id as string,
          userId: absence.userId,
        }),
      );
      this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
        absence.userId,
      ]);
      this.message.success(
        this.translate.instant('general.saved_successfully'),
      );
      this.absenceDrawerHelperService.toggleAbsenceDrawer(false);
      this.store.dispatch([new SetDemarcatorRange([])]);
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } finally {
      this.onGoingRequest.next(false);
    }
  }
}
